import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Receipt, FileText, CheckCircle, Clock, Euro,
  Download, Eye, Search, Loader2, Upload, Trash2, Plus
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const AgencyReceiptsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [receiptsRes, clientsRes] = await Promise.all([
        api.get('/receipts/'),
        api.get('/users/?role=client')
      ]);
      setReceipts(receiptsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Demo data
      setReceipts([
        {
          id: 'rcpt-001',
          client_id: 'client-1',
          client_name: 'HBS Solutions',
          payment_date: '2024-12-20',
          amount: 2400,
          project_name: 'Refonte Site Web HBS',
          payment_method: 'Virement bancaire',
          reference: 'PAY-2024-001',
          invoice_pdf: null,
          status: 'pending_invoice'
        },
        {
          id: 'rcpt-002',
          client_id: 'client-1',
          client_name: 'HBS Solutions',
          payment_date: '2024-12-15',
          amount: 1800,
          project_name: 'Application Mobile HBS Connect',
          payment_method: 'Carte bancaire',
          reference: 'PAY-2024-002',
          invoice_pdf: '/uploads/invoices/invoice-002.pdf',
          status: 'invoice_attached'
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadInvoice = async () => {
    if (!uploadFile || !selectedReceipt) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await api.post(`/receipts/${selectedReceipt.id}/invoice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setReceipts(prev => prev.map(r => 
        r.id === selectedReceipt.id 
          ? { ...r, invoice_pdf: response.data.file_url, status: 'invoice_attached' }
          : r
      ));

      toast.success('Facture uploadée avec succès');
      setShowUploadModal(false);
      setSelectedReceipt(null);
      setUploadFile(null);
    } catch (error) {
      // Demo mode update
      setReceipts(prev => prev.map(r => 
        r.id === selectedReceipt.id 
          ? { ...r, invoice_pdf: '/uploads/invoices/new-invoice.pdf', status: 'invoice_attached' }
          : r
      ));
      toast.success('Facture uploadée avec succès');
      setShowUploadModal(false);
      setSelectedReceipt(null);
      setUploadFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteInvoice = async (receipt) => {
    try {
      await api.delete(`/receipts/${receipt.id}/invoice`);
      setReceipts(prev => prev.map(r => 
        r.id === receipt.id 
          ? { ...r, invoice_pdf: null, status: 'pending_invoice' }
          : r
      ));
      toast.success('Facture supprimée');
    } catch (error) {
      setReceipts(prev => prev.map(r => 
        r.id === receipt.id 
          ? { ...r, invoice_pdf: null, status: 'pending_invoice' }
          : r
      ));
      toast.success('Facture supprimée');
    }
  };

  const filteredReceipts = receipts.filter(receipt =>
    receipt.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    receipt.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    receipt.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: receipts.length,
    pending: receipts.filter(r => r.status === 'pending_invoice').length,
    attached: receipts.filter(r => r.status === 'invoice_attached').length,
    totalAmount: receipts.reduce((sum, r) => sum + (r.amount || 0), 0)
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Gestion des Factures
            </h1>
            <p className="text-[#64748b] mt-1">
              Uploadez et gérez les factures clients
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Receipt className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{stats.total}</p>
                <p className="text-xs text-[#64748b]">Total</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-amber-600">{stats.pending}</p>
                <p className="text-xs text-[#64748b]">À uploader</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-emerald-600">{stats.attached}</p>
                <p className="text-xs text-[#64748b]">Uploadées</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Euro className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>
                  {stats.totalAmount.toLocaleString('fr-FR')}€
                </p>
                <p className="text-xs text-[#64748b]">Total</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="card mb-4">
          <CardContent className="p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
              <Input 
                placeholder="Rechercher par client, projet ou référence..." 
                className="pl-10 h-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.colors?.primary }} />
              </div>
            ) : filteredReceipts.length === 0 ? (
              <div className="text-center py-12 text-[#64748b]">
                <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Aucune facture trouvée</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f8fafc]">
                    <TableHead className="font-medium text-xs">Référence</TableHead>
                    <TableHead className="font-medium text-xs">Client</TableHead>
                    <TableHead className="font-medium text-xs">Projet</TableHead>
                    <TableHead className="font-medium text-xs">Date</TableHead>
                    <TableHead className="font-medium text-xs text-right">Montant</TableHead>
                    <TableHead className="font-medium text-xs">Statut</TableHead>
                    <TableHead className="font-medium text-xs text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReceipts.map((receipt) => (
                    <TableRow key={receipt.id} className="hover:bg-[#f8fafc]/50">
                      <TableCell className="font-mono text-xs text-[#64748b]">
                        {receipt.reference}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                          {receipt.client_name}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-[#64748b]">
                        {receipt.project_name}
                      </TableCell>
                      <TableCell className="text-sm text-[#64748b]">
                        {new Date(receipt.payment_date).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-semibold" style={{ color: theme.colors?.primary }}>
                          {receipt.amount?.toLocaleString('fr-FR')}€
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          "text-[10px]",
                          receipt.status === 'invoice_attached' 
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        )}>
                          {receipt.status === 'invoice_attached' ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Uploadée</>
                          ) : (
                            <><Clock className="w-3 h-3 mr-1" /> En attente</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {receipt.invoice_pdf ? (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-7 w-7 rounded-full"
                                onClick={() => window.open(receipt.invoice_pdf, '_blank')}
                                title="Voir"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-7 w-7 rounded-full text-red-500"
                                onClick={() => handleDeleteInvoice(receipt)}
                                title="Supprimer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-7 text-xs rounded-full"
                              onClick={() => {
                                setSelectedReceipt(receipt);
                                setShowUploadModal(true);
                              }}
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Upload
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Upload Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>
                Uploader une facture
              </DialogTitle>
            </DialogHeader>
            
            {selectedReceipt && (
              <div className="space-y-4">
                <div className="p-3 bg-[#f8fafc] rounded-lg">
                  <p className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                    {selectedReceipt.project_name}
                  </p>
                  <p className="text-xs text-[#64748b]">
                    Client: {selectedReceipt.client_name} • Réf: {selectedReceipt.reference}
                  </p>
                  <p className="text-sm font-mono font-semibold mt-1" style={{ color: theme.colors?.accent }}>
                    {selectedReceipt.amount?.toLocaleString('fr-FR')}€
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Fichier PDF</Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  
                  {uploadFile ? (
                    <div className="flex items-center gap-2 p-3 bg-[#f8fafc] rounded-lg border">
                      <FileText className="w-5 h-5 text-red-500" />
                      <span className="text-sm flex-1 truncate">{uploadFile.name}</span>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setUploadFile(null)}
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Sélectionner un fichier PDF
                    </Button>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleUploadInvoice}
                disabled={!uploadFile || uploading}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Upload...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Uploader
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AgencyReceiptsPage;
